import {
  AddEquation,
  Box3,
  Camera,
  Color,
  ColorRepresentation,
  CustomBlending,
  DataTexture,
  DepthStencilFormat,
  DepthTexture,
  DstAlphaFactor,
  DstColorFactor,
  HalfFloatType,
  Material,
  MeshNormalMaterial,
  NearestFilter,
  NoBlending,
  Object3D,
  RepeatWrapping,
  RGBAFormat,
  Scene,
  ShaderMaterial,
  Texture,
  UniformsUtils,
  UnsignedInt248Type,
  WebGLRenderer,
  WebGLRenderTarget,
  ZeroFactor,
} from "three";
import {
  FullScreenQuad,
  Pass,
} from "three/examples/jsm/postprocessing/Pass.js";
import {
  generateMagicSquareNoise,
  GTAOBlendShader,
} from "three/examples/jsm/shaders/GTAOShader.js";
import {
  generatePdSamplePointInitializer,
  PoissonDenoiseShader,
} from "three/examples/jsm/shaders/PoissonDenoiseShader.js";
import { CopyShader } from "three/examples/jsm/shaders/CopyShader.js";
import { SimplexNoise } from "three/examples/jsm/math/SimplexNoise.js";
import { GTAOAccumulateShader } from "./Shaders/GTAOAccumulateShader";
const _tmpClearColor = new Color();

export interface AccumulatingGTAOPassOptions {
  maxSamples?: number;
  denoise?: boolean;
}

export interface AccumulatingGTAOPassAoParameters {
  radius?: number;
  distanceExponent?: number;
  thickness?: number;
  distanceFallOff?: number;
  scale?: number;
  samples?: number;
  screenSpaceRadius?: boolean;
}

export interface AccumulatingGTAOPassPdParameters {
  lumaPhi?: number;
  depthPhi?: number;
  normalPhi?: number;
  radius?: number;
  radiusExponent?: number;
  rings?: number;
  samples?: number;
  active?: boolean;
}

/**
 * Narrow view of cameras that expose `near` / `far`. Mirrors the cast used in
 * GTAOPass so we can access the frustum without forcing callers to pass a
 * Perspective/Orthographic camera.
 */
interface CameraFrustum {
  near: number;
  far: number;
}

/**
 * Standalone accumulating GTAO pass. Unlike the sibling {@link GTAOPass} it
 * does not inherit the stock output switch; instead the pipeline is fixed:
 *
 *   G-buffer (normals + depth)
 *     -> jittered GTAO compute (GTAOAccumulateShader)
 *     -> running-average accumulation (ping-pong mix shader)
 *     -> optional Poisson denoise on the accumulated AO
 *     -> scene * AO blend onto the read/write buffer
 *
 * Each frame the `tNoise` texture UV offset is jittered via an R2
 * low-discrepancy sequence so consecutive frames produce independent noise
 * samples. The accumulation resets whenever the owning element signals a
 * scene/camera/size change or an interaction start.
 *
 * Sahne **içeriği** değişince (`Engine` → `scene-mutated`, SceneMutationObserver)
 * owning Lit entity `reset()` çağırır; bu Pass içinde event dinlenmez (Pass'ın
 * Engine referansı yok — katman ayrımı).
 */
export class AccumulatingGTAOPass extends Pass {
  public width: number;
  public height: number;
  public override clear: boolean;
  public camera: Camera;
  public scene: Scene;
  public blendIntensity: number;

  public frameIndex = 0;
  public maxSamples: number;
  public denoise: boolean;

  public gtaoNoiseTexture: DataTexture;
  public pdNoiseTexture: DataTexture;

  public gtaoRenderTarget: WebGLRenderTarget;
  public pdRenderTarget: WebGLRenderTarget;
  public normalRenderTarget: WebGLRenderTarget;
  public accumRTs: [WebGLRenderTarget, WebGLRenderTarget];

  public gtaoMaterial: ShaderMaterial;
  public normalMaterial: MeshNormalMaterial;
  public pdMaterial: ShaderMaterial;
  public copyMaterial: ShaderMaterial;
  public blendMaterial: ShaderMaterial;
  public accumMaterial: ShaderMaterial;

  public fsQuad: FullScreenQuad;
  public originalClearColor: Color;

  public depthTexture: DepthTexture;

  public pdRings = 2;
  public pdRadiusExponent = 2;
  public pdSamples = 16;

  private _visibilityCache: Map<Object3D, boolean> = new Map();
  private _reset = false;
  private _accumulate = true;
  /**
   * True once the one-shot denoise has been written into `pdRenderTarget`
   * after convergence. Cleared on reset, max-samples change, or denoise
   * toggle so the denoise is re-applied exactly once on the next converged
   * frame.
   */
  private _denoiseApplied = false;

  /**
   * Optional callback invoked after each accumulated frame while more samples
   * remain. The owning element uses this to request another engine redraw so
   * that accumulation converges without user interaction.
   */
  public _onContinueAccumulation?: () => void;

  constructor(
    scene: Scene,
    camera: Camera,
    width = 256,
    height = 256,
    options?: AccumulatingGTAOPassOptions,
  ) {
    super();

    this.width = width;
    this.height = height;
    this.clear = true;
    this.camera = camera;
    this.scene = scene;
    this.blendIntensity = 1;

    this.maxSamples = options?.maxSamples ?? 256;
    this.denoise = options?.denoise ?? false;

    this.originalClearColor = new Color();
    this.fsQuad = new FullScreenQuad(null as unknown as Material);

    this.gtaoNoiseTexture = generateMagicSquareNoise();
    this.gtaoNoiseTexture.wrapS = RepeatWrapping;
    this.gtaoNoiseTexture.wrapT = RepeatWrapping;
    this.pdNoiseTexture = this._generatePdNoise();

    this.gtaoRenderTarget = new WebGLRenderTarget(this.width, this.height, {
      type: HalfFloatType,
    });
    this.pdRenderTarget = this.gtaoRenderTarget.clone();

    const accumOptions = {
      type: HalfFloatType,
      format: RGBAFormat,
    } as const;
    this.accumRTs = [
      new WebGLRenderTarget(this.width, this.height, accumOptions),
      new WebGLRenderTarget(this.width, this.height, accumOptions),
    ];

    this.depthTexture = new DepthTexture(this.width, this.height);
    this.depthTexture.format = DepthStencilFormat;
    this.depthTexture.type = UnsignedInt248Type;
    this.normalRenderTarget = new WebGLRenderTarget(this.width, this.height, {
      minFilter: NearestFilter,
      magFilter: NearestFilter,
      type: HalfFloatType,
      depthTexture: this.depthTexture,
    });

    this.normalMaterial = new MeshNormalMaterial();
    this.normalMaterial.blending = NoBlending;

    this.gtaoMaterial = new ShaderMaterial({
      defines: Object.assign({}, GTAOAccumulateShader.defines),
      uniforms: UniformsUtils.clone(GTAOAccumulateShader.uniforms),
      vertexShader: GTAOAccumulateShader.vertexShader,
      fragmentShader: GTAOAccumulateShader.fragmentShader,
      blending: NoBlending,
      depthTest: false,
      depthWrite: false,
    });
    this.gtaoMaterial.defines.PERSPECTIVE_CAMERA =
      "isPerspectiveCamera" in this.camera && this.camera.isPerspectiveCamera
        ? 1
        : 0;
    this.gtaoMaterial.defines.NORMAL_VECTOR_TYPE = 1;
    this.gtaoMaterial.defines.DEPTH_SWIZZLING = "x";
    this.gtaoMaterial.uniforms.tNoise.value = this.gtaoNoiseTexture;
    this.gtaoMaterial.uniforms.tNormal.value = this.normalRenderTarget.texture;
    this.gtaoMaterial.uniforms.tDepth.value = this.depthTexture;
    this.gtaoMaterial.uniforms.resolution.value.set(this.width, this.height);
    const frustum = this.camera as unknown as CameraFrustum;
    this.gtaoMaterial.uniforms.cameraNear.value = frustum.near;
    this.gtaoMaterial.uniforms.cameraFar.value = frustum.far;

    this.pdMaterial = new ShaderMaterial({
      defines: Object.assign({}, PoissonDenoiseShader.defines),
      uniforms: UniformsUtils.clone(PoissonDenoiseShader.uniforms),
      vertexShader: PoissonDenoiseShader.vertexShader,
      fragmentShader: PoissonDenoiseShader.fragmentShader,
      depthTest: false,
      depthWrite: false,
    });
    this.pdMaterial.defines.NORMAL_VECTOR_TYPE = 1;
    this.pdMaterial.defines.DEPTH_SWIZZLING = "x";
    this.pdMaterial.uniforms.tDiffuse.value = this.gtaoRenderTarget.texture;
    this.pdMaterial.uniforms.tNormal.value = this.normalRenderTarget.texture;
    this.pdMaterial.uniforms.tDepth.value = this.depthTexture;
    this.pdMaterial.uniforms.tNoise.value = this.pdNoiseTexture;
    this.pdMaterial.uniforms.resolution.value.set(this.width, this.height);
    this.pdMaterial.uniforms.lumaPhi.value = 10;
    this.pdMaterial.uniforms.depthPhi.value = 2;
    this.pdMaterial.uniforms.normalPhi.value = 3;
    this.pdMaterial.uniforms.radius.value = 8;

    this.copyMaterial = new ShaderMaterial({
      uniforms: UniformsUtils.clone(CopyShader.uniforms),
      vertexShader: CopyShader.vertexShader,
      fragmentShader: CopyShader.fragmentShader,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blendSrc: DstColorFactor,
      blendDst: ZeroFactor,
      blendEquation: AddEquation,
      blendSrcAlpha: DstAlphaFactor,
      blendDstAlpha: ZeroFactor,
      blendEquationAlpha: AddEquation,
    });

    this.blendMaterial = new ShaderMaterial({
      uniforms: UniformsUtils.clone(GTAOBlendShader.uniforms),
      vertexShader: GTAOBlendShader.vertexShader,
      fragmentShader: GTAOBlendShader.fragmentShader,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: CustomBlending,
      blendSrc: DstColorFactor,
      blendDst: ZeroFactor,
      blendEquation: AddEquation,
      blendSrcAlpha: DstAlphaFactor,
      blendDstAlpha: ZeroFactor,
      blendEquationAlpha: AddEquation,
    });

    this.accumMaterial = new ShaderMaterial({
      uniforms: {
        newSample: { value: null },
        accumulated: { value: null },
        alpha: { value: 1.0 },
      },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        varying vec2 vUv;
        uniform sampler2D newSample;
        uniform sampler2D accumulated;
        uniform float alpha;
        void main() {
          vec4 prev = texture2D(accumulated, vUv);
          vec4 next = texture2D(newSample, vUv);
          gl_FragColor = mix(prev, next, alpha);
        }
      `,
      blending: NoBlending,
      depthTest: false,
      depthWrite: false,
    });
  }

  public setAccumulate(value: boolean): void {
    this._accumulate = value;
  }

  public getAccumulate(): boolean {
    return this._accumulate;
  }

  /**
   * Reset accumulation. Setting `frameIndex = 0` is sufficient because the
   * first post-reset frame uses `alpha = 1.0`, which fully overwrites whatever
   * stale content the accumulation targets still hold.
   */
  public reset(): void {
   
    this.frameIndex = 0;
    this._reset = true;
    this._denoiseApplied = false;
  }

  /**
   * Update the sample budget without discarding what we already accumulated.
   * If the new limit is larger accumulation resumes from the current sample
   * count; if it is smaller or equal we stay in the converged branch.
   */
  public setMaxSamples(n: number): void {
    const next = Math.max(1, Math.floor(n));
    if (next === this.maxSamples) return;
    this.maxSamples = next;
    this._denoiseApplied = false;
  }

  /**
   * Toggle the one-shot post-convergence denoise. Does not reset the running
   * accumulation; flipping the flag just invalidates any cached denoise so the
   * next converged frame recomputes (or skips) it.
   */
  public setDenoise(enabled: boolean): void {
    if (enabled === this.denoise) return;
    this.denoise = enabled;
    this._denoiseApplied = false;
  }

  public setSceneClipBox(box: Box3 | null | undefined): void {
    if (box) {
      this.gtaoMaterial.needsUpdate =
        this.gtaoMaterial.defines.SCENE_CLIP_BOX !== 1;
      this.gtaoMaterial.defines.SCENE_CLIP_BOX = 1;
      this.gtaoMaterial.uniforms.sceneBoxMin.value.copy(box.min);
      this.gtaoMaterial.uniforms.sceneBoxMax.value.copy(box.max);
    } else {
      this.gtaoMaterial.needsUpdate =
        this.gtaoMaterial.defines.SCENE_CLIP_BOX === 0;
      this.gtaoMaterial.defines.SCENE_CLIP_BOX = 0;
    }
  }

  public updateGtaoMaterial(
    parameters: AccumulatingGTAOPassAoParameters,
  ): void {
    if (parameters.radius !== undefined) {
      this.gtaoMaterial.uniforms.radius.value = parameters.radius;
    }

    if (parameters.distanceExponent !== undefined) {
      this.gtaoMaterial.uniforms.distanceExponent.value =
        parameters.distanceExponent;
    }

    if (parameters.thickness !== undefined) {
      this.gtaoMaterial.uniforms.thickness.value = parameters.thickness;
    }

    if (parameters.distanceFallOff !== undefined) {
      this.gtaoMaterial.uniforms.distanceFallOff.value =
        parameters.distanceFallOff;
      this.gtaoMaterial.needsUpdate = true;
    }

    if (parameters.scale !== undefined) {
      this.gtaoMaterial.uniforms.scale.value = parameters.scale;
    }

    if (
      parameters.samples !== undefined &&
      parameters.samples !== this.gtaoMaterial.defines.SAMPLES
    ) {
      this.gtaoMaterial.defines.SAMPLES = parameters.samples;
      this.gtaoMaterial.needsUpdate = true;
    }

    if (
      parameters.screenSpaceRadius !== undefined &&
      (parameters.screenSpaceRadius ? 1 : 0) !==
        this.gtaoMaterial.defines.SCREEN_SPACE_RADIUS
    ) {
      this.gtaoMaterial.defines.SCREEN_SPACE_RADIUS =
        parameters.screenSpaceRadius ? 1 : 0;
      this.gtaoMaterial.needsUpdate = true;
    }
  }

  public updatePdMaterial(parameters: AccumulatingGTAOPassPdParameters): void {
    let updateShader = false;

    if (parameters.active !== undefined) {
      this.denoise = parameters.active;
    }
    if (parameters.lumaPhi !== undefined) {
      this.pdMaterial.uniforms.lumaPhi.value = parameters.lumaPhi;
    }

    if (parameters.depthPhi !== undefined) {
      this.pdMaterial.uniforms.depthPhi.value = parameters.depthPhi;
    }

    if (parameters.normalPhi !== undefined) {
      this.pdMaterial.uniforms.normalPhi.value = parameters.normalPhi;
    }

    if (parameters.radius !== undefined) {
      this.pdMaterial.uniforms.radius.value = parameters.radius;
    }

    if (
      parameters.radiusExponent !== undefined &&
      parameters.radiusExponent !== this.pdRadiusExponent
    ) {
      this.pdRadiusExponent = parameters.radiusExponent;
      updateShader = true;
    }

    if (parameters.rings !== undefined && parameters.rings !== this.pdRings) {
      this.pdRings = parameters.rings;
      updateShader = true;
    }

    if (
      parameters.samples !== undefined &&
      parameters.samples !== this.pdSamples
    ) {
      this.pdSamples = parameters.samples;
      updateShader = true;
    }

    if (updateShader) {
      this.pdMaterial.defines.SAMPLES = this.pdSamples;
      this.pdMaterial.defines.SAMPLE_VECTORS = generatePdSamplePointInitializer(
        this.pdSamples,
        this.pdRings,
        this.pdRadiusExponent,
      );
      this.pdMaterial.needsUpdate = true;
    }
  }

  override setSize(width: number, height: number): void {
    /*this.width = width;
    this.height = height;
    console.log("setSize", width, height);

    this.gtaoRenderTarget.setSize(width, height);
    this.normalRenderTarget.setSize(width, height);
    this.pdRenderTarget.setSize(width, height);
    for (const rt of this.accumRTs) {
      rt.setSize(width, height);
    }*/

    this.gtaoMaterial.uniforms.resolution.value.set(width* 0.5, height * 0.5);
    this.gtaoMaterial.uniforms.cameraProjectionMatrix.value.copy(
      this.camera.projectionMatrix,
    );
    this.gtaoMaterial.uniforms.cameraProjectionMatrixInverse.value.copy(
      this.camera.projectionMatrixInverse,
    );

    this.pdMaterial.uniforms.resolution.value.set(width* 0.5, height * 0.5  );
    this.pdMaterial.uniforms.cameraProjectionMatrixInverse.value.copy(
      this.camera.projectionMatrixInverse,
    );

    this.frameIndex = 0;
    this._denoiseApplied = false;
  }

  override dispose(): void {
    this.gtaoNoiseTexture.dispose();
    this.pdNoiseTexture.dispose();
    this.normalRenderTarget.dispose();
    this.gtaoRenderTarget.dispose();
    this.pdRenderTarget.dispose();
    for (const rt of this.accumRTs) {
      rt.dispose();
    }
    this.normalMaterial.dispose();
    this.gtaoMaterial.dispose();
    this.pdMaterial.dispose();
    this.copyMaterial.dispose();
    this.blendMaterial.dispose();
    this.accumMaterial.dispose();
    this.fsQuad.dispose();
  }

  override render(
    renderer: WebGLRenderer,
    writeBuffer: WebGLRenderTarget,
    readBuffer: WebGLRenderTarget,
    _deltaTime?: number,
    _maskActive?: boolean,
  ): void {
    if (this._reset || !this._accumulate) {
      this.frameIndex = 0;
      this._clearAccumulationTargets(renderer);
      this._reset = false;
      this._denoiseApplied = false;
    }

    if (!this.enabled) {
      return;
    }

    let aoTexture: Texture;

    if (this.frameIndex < this.maxSamples) {
      // Still converging: render G-buffer, compute jittered GTAO, accumulate.
      this._overrideVisibility();
      this._renderOverride(
        renderer,
        this.normalMaterial,
        this.normalRenderTarget,
        0x7777ff,
        1.0,
      );
      this._restoreVisibility();

      const readRT = this.accumRTs[this.frameIndex % 2];
      const writeRT = this.accumRTs[(this.frameIndex + 1) % 2];

      const offX = (this.frameIndex * 0.7548776662) % 1.0;
      const offY = (this.frameIndex * 0.569840291) % 1.0;
      this.gtaoNoiseTexture.offset.set(offX, offY);
      this.gtaoMaterial.uniforms.noiseOffset.value.set(offX, offY);

      this._updateGtaoCameraUniforms();
      this._renderPass(
        renderer,
        this.gtaoMaterial,
        this.gtaoRenderTarget,
        0xffffff,
        1.0,
      );

      const alpha = 1.0 / (this.frameIndex + 1);
      this.accumMaterial.uniforms.newSample.value =
        this.gtaoRenderTarget.texture;
      this.accumMaterial.uniforms.accumulated.value = readRT.texture;
      this.accumMaterial.uniforms.alpha.value = alpha;
      this._renderPass(renderer, this.accumMaterial, writeRT, 0x000000, 0.0);

      aoTexture = writeRT.texture;
      this.frameIndex++;

      // Accumulation invalidates any prior denoise snapshot.
      this._denoiseApplied = false;

      if (this.frameIndex < this.maxSamples) {
        this._onContinueAccumulation?.();
      } else if (this.denoise) {
        // Just reached the budget: run the one-shot denoise inline so this
        // frame already shows the denoised result, and cache it for reuse.
        this._applyDenoise(renderer, aoTexture);
        aoTexture = this.pdRenderTarget.texture;
        this._denoiseApplied = true;
      }
    } else {
      // Converged: reuse the last accumulated AO. After the last increment
      // the most recently written target is at `frameIndex % 2`.
      const lastRT = this.accumRTs[this.frameIndex % 2];

      if (this.denoise && !this._denoiseApplied) {
        // Post-convergence denoise toggle (or first converged frame): run
        // denoise exactly once and cache the result.
        this._applyDenoise(renderer, lastRT.texture);
        this._denoiseApplied = true;
      }

      aoTexture =
        this.denoise && this._denoiseApplied
          ? this.pdRenderTarget.texture
          : lastRT.texture;
    }

    this._blendToScreen(renderer, aoTexture, writeBuffer, readBuffer);
  }

  /**
   * Run the Poisson denoise on `aoTexture` and write into `pdRenderTarget`.
   * Called at most once per accumulation cycle (on convergence), and again
   * only if `_denoiseApplied` is invalidated (reset, denoise re-toggle,
   * max-samples change).
   */
  private _applyDenoise(renderer: WebGLRenderer, aoTexture: Texture): void {
    this.pdMaterial.uniforms.cameraProjectionMatrixInverse.value.copy(
      this.camera.projectionMatrixInverse,
    );
    this.pdMaterial.uniforms.tDiffuse.value = aoTexture;
    this._renderPass(
      renderer,
      this.pdMaterial,
      this.pdRenderTarget,
      0xffffff,
      1.0,
    );
  }

  /**
   * Sync camera-dependent uniforms and the `PERSPECTIVE_CAMERA` define. The
   * owning element reassigns `this.camera` when the engine's active camera
   * changes, so these must be re-pushed every frame.
   */
  private _updateGtaoCameraUniforms(): void {
    const frustum = this.camera as unknown as CameraFrustum;
    this.gtaoMaterial.uniforms.cameraNear.value = frustum.near;
    this.gtaoMaterial.uniforms.cameraFar.value = frustum.far;
    this.gtaoMaterial.uniforms.cameraProjectionMatrix.value.copy(
      this.camera.projectionMatrix,
    );
    this.gtaoMaterial.uniforms.cameraProjectionMatrixInverse.value.copy(
      this.camera.projectionMatrixInverse,
    );
    this.gtaoMaterial.uniforms.cameraWorldMatrix.value.copy(
      this.camera.matrixWorld,
    );

    const isPerspective =
      "isPerspectiveCamera" in this.camera && this.camera.isPerspectiveCamera
        ? 1
        : 0;
    if (this.gtaoMaterial.defines.PERSPECTIVE_CAMERA !== isPerspective) {
      this.gtaoMaterial.defines.PERSPECTIVE_CAMERA = isPerspective;
      this.gtaoMaterial.needsUpdate = true;
    }
  }

  /**
   * Replicates the stock GTAOPass `OUTPUT.Default` path: first copy the scene
   * (readBuffer) into the write target with no blending, then multiply the AO
   * on top using the pre-configured blendMaterial.
   */
  private _blendToScreen(
    renderer: WebGLRenderer,
    aoTexture: Texture,
    writeBuffer: WebGLRenderTarget,
    readBuffer: WebGLRenderTarget,
  ): void {
    const target = this.renderToScreen ? null : writeBuffer;

    this.copyMaterial.uniforms.tDiffuse.value = readBuffer.texture;
    this.copyMaterial.blending = NoBlending;
    this._renderPass(renderer, this.copyMaterial, target);

    // Restore copyMaterial's custom blending for any other consumers.
    this.copyMaterial.blending = CustomBlending;
    this.copyMaterial.blendSrc = DstColorFactor;
    this.copyMaterial.blendDst = ZeroFactor;
    this.copyMaterial.blendEquation = AddEquation;

    this.blendMaterial.uniforms.intensity.value = this.blendIntensity;
    this.blendMaterial.uniforms.tDiffuse.value = aoTexture;
    this._renderPass(renderer, this.blendMaterial, target);
  }

  /**
   * Clear both ping-pong accumulation targets to transparent black. Preserves
   * the renderer's previously bound render target and clear color/alpha so the
   * rest of the pipeline is unaffected.
   */
  private _clearAccumulationTargets(renderer: WebGLRenderer): void {
    const prevRT = renderer.getRenderTarget();
    const prevClearAlpha = renderer.getClearAlpha();
    renderer.getClearColor(_tmpClearColor);

    renderer.setClearColor(0x000000, 0);
    for (const rt of this.accumRTs) {
      renderer.setRenderTarget(rt);
      renderer.clear(true, false, false);
    }

    renderer.setRenderTarget(prevRT);
    renderer.setClearColor(_tmpClearColor, prevClearAlpha);
  }

  private _renderPass(
    renderer: WebGLRenderer,
    passMaterial: Material,
    renderTarget: WebGLRenderTarget | null,
    clearColor?: ColorRepresentation,
    clearAlpha?: number,
  ): void {
    renderer.getClearColor(this.originalClearColor);
    const originalClearAlpha = renderer.getClearAlpha();
    const originalAutoClear = renderer.autoClear;

    renderer.setRenderTarget(renderTarget);

    renderer.autoClear = false;
    if (clearColor !== undefined && clearColor !== null) {
      renderer.setClearColor(clearColor);
      renderer.setClearAlpha(clearAlpha || 0.0);
      renderer.clear();
    }

    this.fsQuad.material = passMaterial;
    this.fsQuad.render(renderer);

    renderer.autoClear = originalAutoClear;
    renderer.setClearColor(this.originalClearColor);
    renderer.setClearAlpha(originalClearAlpha);
  }

  private _renderOverride(
    renderer: WebGLRenderer,
    overrideMaterial: Material,
    renderTarget: WebGLRenderTarget | null,
    clearColor?: ColorRepresentation,
    clearAlpha?: number,
  ): void {
    renderer.getClearColor(this.originalClearColor);
    const originalClearAlpha = renderer.getClearAlpha();
    const originalAutoClear = renderer.autoClear;

    renderer.setRenderTarget(renderTarget);
    renderer.autoClear = false;

    const material = overrideMaterial as Material & {
      clearColor?: ColorRepresentation;
      clearAlpha?: number;
    };
    clearColor = material.clearColor || clearColor;
    clearAlpha = material.clearAlpha || clearAlpha;

    if (clearColor !== undefined && clearColor !== null) {
      renderer.setClearColor(clearColor);
      renderer.setClearAlpha(clearAlpha || 0.0);
      renderer.clear();
    }

    this.scene.overrideMaterial = overrideMaterial;
    renderer.render(this.scene, this.camera);
    this.scene.overrideMaterial = null;

    renderer.autoClear = originalAutoClear;
    renderer.setClearColor(this.originalClearColor);
    renderer.setClearAlpha(originalClearAlpha);
  }

  private _overrideVisibility(): void {
    const scene = this.scene;
    const cache = this._visibilityCache;

    scene.traverse((object: Object3D) => {
      cache.set(object, object.visible);

      const maybeTyped = object as Object3D & {
        isPoints?: boolean;
        isLine?: boolean;
      };
      if (
        maybeTyped.isPoints ||
        maybeTyped.isLine ||
        object.userData.contributeToAO === "false"
      ) {
        object.visible = false;
      }
    });
  }

  private _restoreVisibility(): void {
    const scene = this.scene;
    const cache = this._visibilityCache;

    scene.traverse((object: Object3D) => {
      const visible = cache.get(object);
      if (visible !== undefined) {
        object.visible = visible;
      }
    });

    cache.clear();
  }

  /**
   * Matches the noise texture used by three's GTAOPass: 64x64 RGBA simplex
   * noise driving the Poisson denoise jitter. Copied here so this pass no
   * longer has to extend GTAOPass just to reuse the helper.
   */
  private _generatePdNoise(size = 64): DataTexture {
    const simplex = new SimplexNoise();

    const arraySize = size * size * 4;
    const data = new Uint8Array(arraySize);

    for (let i = 0; i < size; i++) {
      for (let j = 0; j < size; j++) {
        const x = i;
        const y = j;

        data[(i * size + j) * 4] = (simplex.noise(x, y) * 0.5 + 0.5) * 255;
        data[(i * size + j) * 4 + 1] =
          (simplex.noise(x + size, y) * 0.5 + 0.5) * 255;
        data[(i * size + j) * 4 + 2] =
          (simplex.noise(x, y + size) * 0.5 + 0.5) * 255;
        data[(i * size + j) * 4 + 3] =
          (simplex.noise(x + size, y + size) * 0.5 + 0.5) * 255;
      }
    }

    const noiseTexture = new DataTexture(data, size, size, RGBAFormat);
    noiseTexture.wrapS = RepeatWrapping;
    noiseTexture.wrapT = RepeatWrapping;
    noiseTexture.needsUpdate = true;

    return noiseTexture;
  }
}
